import type { SpectralWorkspaceSession } from '../types';
import type { SessionFullTractMetrics } from './computeSessionFullTractMetrics';

export type GroupSummary = {
  totalSessions: number;
  readySessions: number;
  perfectSessions: number;
  sessionsWithEventIssues: number;
  meanHr: number | null;
  meanHrStd: number | null;
  meanLfHfRatio: number | null;
  meanLfHfRatioStd: number | null;
  outlierCount: number;
  outlierSessionIds: number[];
};

const meanOf = (values: number[]) =>
  values.length > 0
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : null;

const sampleStdOf = (values: number[], mean: number | null) => {
  if (values.length < 2 || mean == null) return null;

  return Math.sqrt(
    values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
      (values.length - 1)
  );
};

const isPerfectSession = (session: SpectralWorkspaceSession) =>
  session.repairStatus === 'perfect' ||
  session.repairStatus === 'repaired_perfect';

const hasEventIssues = (session: SpectralWorkspaceSession) =>
  session.repairStatus === 'needs_repair';

const invert2x2Matrix = (matrix: [[number, number], [number, number]]) => {
  const determinant = matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0];
  if (Math.abs(determinant) < 1e-8) {
    return [
      [1, 0],
      [0, 1],
    ] as [[number, number], [number, number]];
  }

  return [
    [matrix[1][1] / determinant, -matrix[0][1] / determinant],
    [-matrix[1][0] / determinant, matrix[0][0] / determinant],
  ] as [[number, number], [number, number]];
};

const detectOutlierSessionIds = (
  metrics: SessionFullTractMetrics[]
): number[] => {
  const points = metrics.filter(
    (
      item
    ): item is SessionFullTractMetrics & {
      meanHr: number;
      lfHfRatio: number;
    } =>
      item.meanHr != null &&
      Number.isFinite(item.meanHr) &&
      item.lfHfRatio != null &&
      Number.isFinite(item.lfHfRatio)
  );

  if (points.length < 3) return [];

  const meanHr = meanOf(points.map(point => point.meanHr)) ?? 0;
  const meanLfHf = meanOf(points.map(point => point.lfHfRatio)) ?? 0;
  const covarianceXX =
    meanOf(points.map(point => (point.meanHr - meanHr) ** 2)) ?? 1e-6;
  const covarianceYY =
    meanOf(points.map(point => (point.lfHfRatio - meanLfHf) ** 2)) ?? 1e-6;
  const covarianceXY =
    meanOf(
      points.map(
        point => (point.meanHr - meanHr) * (point.lfHfRatio - meanLfHf)
      )
    ) ?? 0;
  const inverseCovariance = invert2x2Matrix([
    [covarianceXX, covarianceXY],
    [covarianceXY, covarianceYY],
  ]);

  return points
    .map(point => {
      const dx = point.meanHr - meanHr;
      const dy = point.lfHfRatio - meanLfHf;
      const distanceSquared =
        dx * (inverseCovariance[0][0] * dx + inverseCovariance[0][1] * dy) +
        dy * (inverseCovariance[1][0] * dx + inverseCovariance[1][1] * dy);
      const distance = Math.sqrt(Math.max(distanceSquared, 0));

      return {
        sessionId: point.sessionId,
        distance,
      };
    })
    .filter(point => point.distance > 3)
    .map(point => point.sessionId);
};

export const computeGroupSummary = (
  sessions: SpectralWorkspaceSession[],
  metrics: SessionFullTractMetrics[]
): GroupSummary => {
  const readySessions = sessions.filter(session => session.status === 'ready');
  const hrValues = metrics
    .map(item => item.meanHr)
    .filter(
      (value): value is number => value != null && Number.isFinite(value)
    );
  const lfHfValues = metrics
    .map(item => item.lfHfRatio)
    .filter(
      (value): value is number => value != null && Number.isFinite(value)
    );

  const meanHr = meanOf(hrValues);
  const meanLfHfRatio = meanOf(lfHfValues);
  const outlierSessionIds = detectOutlierSessionIds(metrics);

  return {
    totalSessions: sessions.length,
    readySessions: readySessions.length,
    perfectSessions: readySessions.filter(isPerfectSession).length,
    sessionsWithEventIssues: readySessions.filter(hasEventIssues).length,
    meanHr,
    meanHrStd: sampleStdOf(hrValues, meanHr),
    meanLfHfRatio,
    meanLfHfRatioStd: sampleStdOf(lfHfValues, meanLfHfRatio),
    outlierCount: outlierSessionIds.length,
    outlierSessionIds,
  };
};
